import * as React from 'react';
import noop from 'lodash/noop';
import {styledDiv, useTheme} from '@packages/utilities/styled';
import {FinePrint} from '@packages/systems/design-system/Text';
import {Link} from '@packages/systems/design-system/Link';

import {showDialog} from '../state';
import {CancelButton, Dialog, SubmitButton} from './shared';
import type {ButtonType, IconType, Size, BaseButtonType} from './shared';
import {Tooltip, TextInput} from '@packages/systems/spring';

type ErrorTooltip = string;

type PromptType = {
  label: string;
  placeholder?: string;
  defaultValue?: string;
  validate?: (value: string) => boolean | ErrorTooltip;
};

type PromptConfig = {
  cancel?: BaseButtonType;
  content?: React.ReactNode;
  iconType?: IconType;
  onCancel?: () => void;
  onSubmit?: (value: Array<string>) => void;
  prompts: Array<PromptType>;
  size?: Size;
  submit?: ButtonType;
  // The React.Element type acts as an escape hatch
  // for Logic to implement breadcrumbs.
  title: string | React.ReactElement;
  readMore?: {
    text: string;
    href: string;
  };
};

type PromptDialogProps = PromptConfig & {
  onSubmit: NonNullable<PromptConfig['onSubmit']>;
  onCancel: NonNullable<PromptConfig['onCancel']>;
};

type PromptValueType = {
  value: string;
  invalid: boolean;
};

type ChangeFnType = ({
  oldPrompt,
  promptValue,
}: {
  oldPrompt: PromptType;
  promptValue: PromptValueType;
}) => void;

type InputPropsType = {
  isFirst: boolean;
  prompt: PromptType;
  onChange: ChangeFnType;
  promptValue: PromptValueType;
  onEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  'data-automation-id': string;
};

const Input: React.FC<InputPropsType> = ({
  isFirst,
  prompt,
  onChange,
  promptValue,
  onEnter,
  'data-automation-id': dataAutomationId,
}) => {
  const {label, placeholder, validate = validateTrue} = prompt;
  const {
    gutters,
    typography: {finePrint},
  } = useTheme();
  const errorLabelRef = React.useRef<string | undefined>(undefined);
  const [shouldShowErrorTooltip, setShouldShowErrorTooltip] =
    React.useState<boolean>(false);

  return (
    <div
      key={label}
      style={{
        display: 'grid',
        gridRowGap: gutters.x4 * 2,
        marginTop: isFirst ? undefined : gutters.base * 2,
        fontSize: finePrint.fontSize,
      }}
    >
      <FinePrint>{label}</FinePrint>

      <Tooltip
        colorVariant="danger"
        label={errorLabelRef.current}
        isOpen={shouldShowErrorTooltip && Boolean(errorLabelRef.current)}
        asChild
      >
        <TextInput
          isError={promptValue.invalid}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={isFirst}
          value={promptValue.value}
          placeholder={placeholder}
          onChange={(e) => {
            const isValidResult = validate(e.target.value);
            errorLabelRef.current =
              typeof isValidResult === 'string'
                ? isValidResult
                : isValidResult
                  ? errorLabelRef.current
                  : undefined;

            setShouldShowErrorTooltip(
              typeof isValidResult === 'string' || !isValidResult
            );

            onChange({
              oldPrompt: prompt,
              promptValue: {
                value: e.target.value,
                invalid:
                  isValidResult === false || typeof isValidResult === 'string',
              },
            });
          }}
          onKeyDown={onEnter}
          data-automation-id={dataAutomationId}
        />
      </Tooltip>
    </div>
  );
};

type InputsPropsType = {
  onChange: ChangeFnType;
  promptValues: Array<PromptValueType>;
  prompts: Array<PromptType>;
  onEnter: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const Inputs: React.FC<InputsPropsType> = ({
  onChange,
  promptValues,
  prompts,
  onEnter,
}) => {
  return (
    <>
      {prompts.map((prompt, index) => (
        <Input
          key={prompt.label}
          isFirst={index === 0}
          prompt={prompt}
          // @ts-expect-error - TS2322 - Type 'PromptValueType | undefined' is not assignable to type 'PromptValueType'.
          promptValue={promptValues[index]}
          onChange={onChange}
          onEnter={onEnter}
          data-automation-id={`prompt-dialog-input-${index}`}
        />
      ))}
    </>
  );
};

const PromptContent = styledDiv(({theme: {gutters}}) => ({
  marginBottom: gutters.wide,
}));

const validateTrue = (_value: string) => true;

export const PromptDialog = React.memo<PromptDialogProps>(function Prompt({
  cancel = {label: 'Cancel', intent: 'default'},
  content,
  iconType,
  onCancel,
  onSubmit,
  prompts,
  size,
  submit = {
    label: 'Submit',
    intent: 'primary',
    disabled: false,
    'data-automation-id': null,
  },
  title,
  readMore,
}) {
  const [promptValues, setPromptValues] = React.useState(
    prompts.map(({defaultValue}) => ({
      value: defaultValue || '',
      invalid: false,
    }))
  );

  const invalid = React.useMemo(
    () =>
      promptValues.some(({value}, i) => {
        const prompt = prompts[i];
        // @ts-expect-error - TS18048 - 'prompt' is possibly 'undefined'.
        const validate = prompt.validate || validateTrue;
        const isValid = validate(value);
        return isValid === false || typeof isValid === 'string';
      }),
    [prompts, promptValues]
  );

  const handleConfirm = React.useCallback(() => {
    if (invalid) {
      return;
    }

    onSubmit(promptValues.map((p) => p.value));
  }, [invalid, onSubmit, promptValues]);

  const handleEnter = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  return (
    <Dialog
      content={
        <>
          {content ? <PromptContent>{content}</PromptContent> : null}

          <Inputs
            prompts={prompts}
            promptValues={promptValues}
            onEnter={handleEnter}
            onChange={({oldPrompt, promptValue}) => {
              setPromptValues(
                promptValues.map((prevPromptValue, idx) =>
                  oldPrompt === prompts[idx] ? promptValue : prevPromptValue
                )
              );
            }}
          />
        </>
      }
      buttons={
        <>
          {readMore && (
            <Link
              style={{alignItems: 'center'}}
              primary
              isExternal
              href={readMore.href}
            >
              {readMore.text}
            </Link>
          )}
          <CancelButton
            danger={cancel.intent === 'danger'}
            data-automation-id={cancel.label}
            onClick={() => onCancel()}
            primary={cancel.intent === 'primary'}
          >
            {cancel.label}
          </CancelButton>
          <SubmitButton
            danger={submit.intent === 'danger'}
            data-automation-id={submit['data-automation-id'] || submit.label}
            disabled={submit.disabled || invalid}
            onClick={handleConfirm}
            primary={submit.intent === 'primary'}
          >
            {submit.label}
          </SubmitButton>
        </>
      }
      iconType={iconType}
      onClose={() => onCancel()}
      size={size}
      title={title}
    />
  );
});

export const prompt = ({
  onCancel = noop,
  onSubmit = noop,
  ...staticProps
}: PromptConfig) =>
  showDialog<string[]>(function PromptWrapper({
    onCancel: internalCancel,
    onSubmit: internalSubmit,
  }) {
    const handleSubmit = React.useCallback(
      (response: string[]) => {
        onSubmit(response);
        internalSubmit(response);
      },
      [internalSubmit]
    );

    const handleCancel = React.useCallback(() => {
      onCancel();
      internalCancel();
    }, [internalCancel]);

    return (
      <PromptDialog
        {...staticProps}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    );
  });
